import {
  Badge,
  Card,
  CardActionArea,
  Typography,
  useTheme
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import {
  PROFESSIONALS,
  LOGIN,
  PATIENT_LIST,
  ESTABLISHMENTS,
  MEDICAL_EVENTS,
  CXRAY_ANALYSIS,
  ECG_ANALYSIS
} from 'navigation/CONSTANTS'
import { Navigate } from 'react-router-dom'
import Storage from 'storage/auth'
import NavLinkBase from 'design-system/NavLink'
import { useAppSelector } from 'reduxStore/store'
import {
  wrapWithPermissionsByAdminRole,
  wrapWithPermissionsByModuleName,
  wrapWithPermissionsByRole
} from 'components/rules/PermissionByRole'
import { IProssionalRoleTypeEnum } from 'types/user/enum'
import { IModulePermissionType } from 'types'
import { useMemo } from 'react'

interface CardProps {
  link: string
  title: string
  icon: React.ReactElement
  color: string
  aiPowered?: boolean
}

interface CardData extends Omit<CardProps, 'link'> {
  label: string
  superAdminOnly?: boolean
  rolesAllowed?: IProssionalRoleTypeEnum[]
  modulesRequired?: IModulePermissionType[]
  badge?: string
}

export const Cards = (miniDrawer?: boolean): Record<string, CardData> => ({
  [PATIENT_LIST]: {
    title: 'Monitoramento de Pacientes',
    icon: (
      <span
        style={
          miniDrawer
            ? { color: 'var(--secondarySoft-button-color)' }
            : undefined
        }
        className='material-symbols-outlined'
      >
        diversity_1
      </span>
    ),
    color: '#4A6FA5',
    modulesRequired: ['patient_monitoring'],
    label: 'Pacientes'
  },
  [CXRAY_ANALYSIS]: {
    title: 'Análise de Raio-X',
    icon: (
      <span
        style={
          miniDrawer
            ? { color: 'var(--secondarySoft-button-color)' }
            : undefined
        }
        className='material-symbols-outlined'
      >
        radiology
      </span>
    ),
    color: '#6F5287',
    aiPowered: true,
    label: 'Análise de Raio-X',
    modulesRequired: ['cxray_analysis']
  },
  [ECG_ANALYSIS]: {
    title: 'Análise de ECG',
    icon: (
      <span
        style={
          miniDrawer
            ? { color: 'var(--secondarySoft-button-color)' }
            : undefined
        }
        className='material-symbols-outlined'
      >
        ecg
      </span>
    ),
    color: '#875A94',
    aiPowered: true,
    label: 'Análise de ECG',
    modulesRequired: ['ecg_analysis']
  },
  [PROFESSIONALS]: {
    title: 'Gestão de Colaboradores',
    icon: (
      <span
        style={
          miniDrawer
            ? { color: 'var(--secondarySoft-button-color)' }
            : undefined
        }
        className='material-symbols-outlined'
      >
        clinical_notes
      </span>
    ),
    color: '#5A669D',
    rolesAllowed: [IProssionalRoleTypeEnum.administrator],
    label: 'Colaboradores'
  },
  [ESTABLISHMENTS]: {
    title: 'Gestão de Estabelecimentos',
    icon: (
      <span
        style={
          miniDrawer
            ? { color: 'var(--secondarySoft-button-color)' }
            : undefined
        }
        className='material-symbols-outlined'
      >
        domain_add
      </span>
    ),
    color: '#685E94',
    superAdminOnly: true,
    label: 'Estabelecimentos'
  },
  [MEDICAL_EVENTS]: {
    title: 'Gestão de Eventos Médicos',
    icon: (
      <span
        style={
          miniDrawer
            ? { color: 'var(--secondarySoft-button-color)' }
            : undefined
        }
        className='material-symbols-outlined'
      >
        diagnosis
      </span>
    ),
    color: '#75568C',
    superAdminOnly: true,
    label: 'Eventos Médicos'
  }
})

const cardEntries = Object.entries(Cards())

const wrapWithBadge = (
  component: React.ReactElement,
  badgeContent?: string
) => {
  return badgeContent ? (
    <Badge
      color='info'
      badgeContent={badgeContent}
      sx={{
        width: '100%',
        '& a': {
          width: '100%'
        }
      }}
    >
      {component}
    </Badge>
  ) : (
    component
  )
}

const HomePage = () => {
  const user = Storage.user.get()

  const establishmentId = useAppSelector(
    rState => rState.establishment.data?.establishment_id
  )

  const theme = useTheme()

  const textStyle = useMemo(
    () =>
      theme.palette.mode == 'dark'
        ? {
            color: theme.palette.text.primary
          }
        : {
            color: theme.palette.primarySoft.main
          },
    [theme.palette.mode]
  )

  const CardLink: React.FC<CardProps> = ({
    link,
    title,
    icon,
    color,
    aiPowered
  }) => (
    <NavLinkBase to={link} absolute>
      <Card
        sx={{
          background: color,
          color: 'common.white',
          height: '120px',
          display: 'flex',
          flexDirection: 'column',

          '&:hover': { div: { transform: 'translateX(5px)' } }
        }}
      >
        <CardActionArea
          sx={{
            p: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            position: 'relative'
          }}
        >
          {aiPowered && (
            <div
              style={{
                position: 'absolute',
                left: '16px',
                bottom: '16px'
              }}
              aria-label='AI Powered'
            >
              <span
                className='material-symbols-outlined'
                style={{
                  fontSize: '32px',
                  verticalAlign: 'middle',
                  marginRight: '5px',
                  opacity: 0.8
                }}
              >
                auto_awesome
              </span>
            </div>
          )}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {icon}
            <Typography variant='h6' textAlign='center'>
              {title}
            </Typography>
          </div>
          <div style={{ width: '100%', textAlign: 'right' }}>
            <span
              className='material-symbols-outlined'
              style={{ fontSize: '20px', opacity: 0.8 }}
            >
              arrow_forward
            </span>
          </div>
        </CardActionArea>
      </Card>
    </NavLinkBase>
  )

  if (user === null) {
    return <Navigate to={LOGIN} />
  }

  if (establishmentId === null) {
    return <div></div>
  }

  return (
    <Grid
      container
      spacing={2}
      display='flex'
      textAlign='center'
      justifyContent='center'
      alignItems='center'
      height='100%'
      p={3}
      sx={{ margin: '0 auto', width: '100%', maxWidth: '1200px' }}
    >
      <Grid
        size={12}
        display='flex'
        flexDirection='column'
        textAlign='center'
        gap={2}
      >
        <Typography variant='h5' sx={textStyle}>
          Bem-vindo, <b>{user.first_name}</b>!
        </Typography>
        <Typography variant='h5' sx={textStyle}>
          Como a <b>plataforma sentinela</b> pode te ajudar?
        </Typography>
      </Grid>
      <Grid mt={3} container size={12} spacing={2} justifyContent='center'>
        {cardEntries
          .filter(([_, card]) => !card.superAdminOnly)
          .map(([link, card]) => {
            const baseCard = wrapWithBadge(
              <CardLink
                link={link}
                title={card.title}
                icon={card.icon}
                color={card.color}
                aiPowered={card.aiPowered}
              />,
              card.badge
            )

            return wrapWithPermissionsByModuleName(
              wrapWithPermissionsByRole(
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={`base-${link}`}>
                  {baseCard}
                </Grid>,
                !!card.rolesAllowed?.length,
                card.rolesAllowed ?? []
              ),
              (card.modulesRequired?.length ?? 0) > 0,
              card.modulesRequired ?? [],
              'or'
            )
          })}
      </Grid>
      {user.super_admin && (
        <>
          <Grid
            size={12}
            display='flex'
            flexDirection='column'
            textAlign='center'
            gap={2}
            mt={2}
          >
            <Typography variant='h5' sx={textStyle}>
              Acesso apenas para Administradores
            </Typography>
          </Grid>
          <Grid mt={3} container size={12} spacing={2} justifyContent='center'>
            {cardEntries
              .filter(([_, card]) => card.superAdminOnly)
              .map(([link, card]) => {
                const baseCard = wrapWithBadge(
                  <CardLink
                    link={link}
                    title={card.title}
                    icon={card.icon}
                    color={card.color}
                    aiPowered={card.aiPowered}
                  />,
                  card.badge
                )

                return (
                  <Grid size={{ xs: 12, md: 6, lg: 4 }} key={link}>
                    {wrapWithPermissionsByModuleName(
                      wrapWithPermissionsByAdminRole(
                        wrapWithPermissionsByRole(
                          baseCard,
                          !!card.rolesAllowed?.length,
                          card.rolesAllowed ?? []
                        ),
                        !!card.superAdminOnly
                      ),
                      (card.modulesRequired?.length ?? 0) > 0,
                      card.modulesRequired!
                    )}
                  </Grid>
                )
              })}
          </Grid>
        </>
      )}
    </Grid>
  )
}

export default HomePage
